import { useMemo } from 'react'
import { AchievementBadge } from './AchievementBadge'
import type { Achievement, AchievementCategory } from '@/types'
import { cn } from '@/lib/utils'

interface AchievementGridProps {
  achievements: Record<string, Achievement>
  category?: AchievementCategory | 'all'
  showLocked?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export function AchievementGrid({
  achievements,
  category = 'all',
  showLocked = true,
  size = 'md',
}: AchievementGridProps) {
  const filteredAchievements = useMemo(() => {
    let list = Object.values(achievements)
    
    if (category !== 'all') {
      list = list.filter(a => a.category === category)
    }
    
    if (!showLocked) {
      list = list.filter(a => a.unlockedAt)
    }
    
    // Sort: unlocked first, then by points (descending)
    return list.sort((a, b) => {
      if (a.unlockedAt && !b.unlockedAt) return -1
      if (!a.unlockedAt && b.unlockedAt) return 1
      return b.points - a.points
    })
  }, [achievements, category, showLocked])

  if (filteredAchievements.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No achievements {category !== 'all' && `in ${category}`} yet
      </div>
    )
  }

  return (
    <div className={cn(
      'grid gap-4',
      size === 'sm' && 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8',
      size === 'md' && 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6',
      size === 'lg' && 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
    )}>
      {filteredAchievements.map((achievement) => (
        <AchievementBadge
          key={achievement.id}
          achievement={achievement}
          size={size}
        />
      ))}
    </div>
  )
}
